import { CdkListboxModule } from '@angular/cdk/listbox'
import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnChanges,
  OnDestroy,
  signal
} from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { fadeIn, fadeInOut, listAnimation } from '@core/animations'
import { CurrencyMaskDirective } from '@core/directives/currency-mask/currency-mask.directive'
import {
  PalantirButtonComponent,
  PalantirConfirmDialogService,
  PalantirContentComponent,
  PalantirDropdownMenuModule,
  PalantirIconComponent,
  PalantirInputComponent,
  PalantirLayoutComponent,
  PalantirRadioComponent,
  PalantirTextComponent,
  PalantirLabelColorsEnum,
} from '@fieldcontrol/palantir'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { addDays, format } from 'date-fns'
import { cloneDeep } from 'lodash-es'
import { NgScrollbar } from 'ngx-scrollbar'
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs'
import { PipesModule } from '../../../../core/pipes/pipes.module'
import { WarrantiesMapperService } from '../../services/warranties.mapper.service'
import { MaintenanceTabWarrantiesService } from '../../services/warranties.service'
import { WARRANTY_TYPE, WarrantyForm } from '../../warranties.type'

@Component({
    selector: 'maintenance-warranty-detail',
    templateUrl: './warranty-detail.component.html',
    styleUrls: ['./warranty-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [listAnimation, fadeIn, fadeInOut],
    imports: [
        CommonModule,
        PalantirLayoutComponent,
        PalantirDropdownMenuModule,
        PalantirContentComponent,
        PalantirTextComponent,
        PalantirIconComponent,
        PalantirButtonComponent,
        PalantirInputComponent,
        PalantirRadioComponent,
        CurrencyMaskDirective,
        NgScrollbar,
        ReactiveFormsModule,
        CdkListboxModule,
        PipesModule,
        TranslateModule,
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ],
    providers: [WarrantiesMapperService]
})
export class MaintenanceWarrantyDetailComponent implements OnDestroy, OnChanges {
  @Input({ required: true }) public warrantyForm!: FormGroup<WarrantyForm>

  public warrantyEditForm: FormGroup<WarrantyForm> | null = null
  public isViewMode = signal(true)

  private destroy$ = new Subject<void>()
  private editDestroy$ = new Subject<void>()

  public WARRANTY_TYPE = WARRANTY_TYPE
  public tag = signal<({ color: PalantirLabelColorsEnum, label: string })>(this.maintenanceTabWarrantiesService.mapperTag(this.warrantyForm))

  public constructor(
    public readonly maintenanceTabWarrantiesService: MaintenanceTabWarrantiesService,
    private readonly warrantiesMapperService: WarrantiesMapperService,
    private readonly palantirConfirmDialogService: PalantirConfirmDialogService,
    private readonly translateService: TranslateService
  ) { }

  public ngOnChanges(): void {
    this.warrantyEditForm = null
    this.isViewMode.set(true)
  }

  public ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
    this.clearEditObservers()
  }

  public toggleEdit(): void {
    if (!this.isViewMode) {
      this.cancelEdit()
      return
    }

    this.clearEditObservers()
    this.isViewMode.set(false)
    this.warrantyEditForm = cloneDeep(this.warrantyForm)
    this.observeCoverageStartForForm(this.warrantyEditForm!)
  }

  public async saveWarranty(): Promise<void> {
    if (this.warrantyEditForm?.valid && !this.isViewMode()) {
      const warrantiesForms = this.maintenanceTabWarrantiesService.warrantiesForm()
      const index = this.maintenanceTabWarrantiesService.selectedWarrantyFormIndex()

      if (index === null) return

      warrantiesForms[index].patchValue(this.warrantyEditForm.getRawValue())
      this.maintenanceTabWarrantiesService.warrantiesForm.set(warrantiesForms)
      this.maintenanceTabWarrantiesService.eventRequests[index] = this.warrantiesMapperService.toCreateInput(this.warrantyEditForm)
      const warrantyId = this.warrantyEditForm.get('id')?.value
      const maintenanceId = this.maintenanceTabWarrantiesService.maintenanceId()

      if (maintenanceId && warrantyId) {
        await this.maintenanceTabWarrantiesService.updateWarranty(this.warrantyForm)
      }

      this.maintenanceTabWarrantiesService.sendWarrantiesOnEvent()

      this.cancelEdit()
    }
  }

  public cancelEdit(): void {
    this.clearEditObservers()
    this.warrantyEditForm = null
    this.isViewMode.set(true)
  }

  public async removeWarranty(): Promise<void> {
    const dialog = this.palantirConfirmDialogService.openDialog({
      title: this.translateService.instant('TAB_WARRANTIES_MAINTENANCE.DIALOG_REMOVE.TITLE'),
      text: this.translateService.instant('TAB_WARRANTIES_MAINTENANCE.DIALOG_REMOVE.INFO'),
      cancelText: this.translateService.instant('TAB_WARRANTIES_MAINTENANCE.DIALOG_REMOVE.BUTTONS.CANCEL'),
      successText: this.translateService.instant('TAB_WARRANTIES_MAINTENANCE.DIALOG_REMOVE.BUTTONS.REMOVE'),
    })

    const result = await dialog.afterClosed<{ role: string }>()

    if (result?.role !== 'success') return

    this.cancelEdit()
    const maintenanceId = this.maintenanceTabWarrantiesService.maintenanceId()
    const warrantyId = this.warrantyForm.get('id')?.value

    if(maintenanceId && warrantyId) {
      await this.maintenanceTabWarrantiesService.removeWarranty(warrantyId)
    }

    const warrantiesForms = this.maintenanceTabWarrantiesService.warrantiesForm()
    const index = this.maintenanceTabWarrantiesService.selectedWarrantyFormIndex()

    if (index === null) return

    warrantiesForms.splice(index, 1)
    this.maintenanceTabWarrantiesService.selectedWarrantyForm = undefined
    this.maintenanceTabWarrantiesService.warrantiesForm.set(warrantiesForms)
    this.maintenanceTabWarrantiesService.eventRequests.splice(index, 1)
    this.maintenanceTabWarrantiesService.setHasWarranties()

    const firstWarranty = this.maintenanceTabWarrantiesService.getFirstWarranty(WARRANTY_TYPE.MAINTENANCES)
    if (firstWarranty) {
      this.maintenanceTabWarrantiesService.selectWarranty(firstWarranty, 0)
    }
  }

  private observeCoverageStartForForm(form: FormGroup<WarrantyForm>): void {
    form.get('coverageDurationDays')?.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.editDestroy$))
      .subscribe((value) => {
        const coverageStartedAt = form.get('coverageStartedAt')?.value
        const endDate = coverageStartedAt && value !== null
          ? format(addDays(new Date(coverageStartedAt), value), 'yyyy-MM-dd')
          : null
        form.get('coverageEndedAt')?.setValue(endDate)

        const extendedCoverageDurationDays = form.get('extendedCoverageDurationDays')?.value
        const extendEndDate = coverageStartedAt
          ? format(addDays(new Date(coverageStartedAt), Number(extendedCoverageDurationDays) + Number(value)), 'yyyy-MM-dd')
          : null

        form.get('extendedCoverageEndedAt')?.setValue(extendEndDate)
      })

    form.get('extendedCoverageDurationDays')?.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.editDestroy$))
      .subscribe((value) => {
        const coverageStartedAt = form.get('coverageStartedAt')?.value
        const coverageDurationDays = form.get('coverageDurationDays')?.value
        const extendEndDate = coverageStartedAt
          ? format(addDays(new Date(coverageStartedAt), Number(coverageDurationDays) + Number(value)), 'yyyy-MM-dd')
          : null
        form.get('extendedCoverageEndedAt')?.setValue(extendEndDate)
      })

    form.get('hasCoverageExtend')?.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.editDestroy$))
      .subscribe((value) => {
        if (value) {
          this.toggleValidatorField(true, form.get('extendedCoverageDurationDays') as FormControl)
          this.toggleValidatorField(true, form.get('extendedValue') as FormControl)
        } else {
          form.get('extendedCoverageDurationDays')?.reset()
          form.get('extendedValue')?.reset()
          this.toggleValidatorField(false, form.get('extendedCoverageDurationDays') as FormControl)
          this.toggleValidatorField(false, form.get('extendedValue') as FormControl)
        }
      })
  }

  private clearEditObservers(): void {
    this.editDestroy$.next()
    this.editDestroy$.complete()
    this.editDestroy$ = new Subject<void>()
  }

  private toggleValidatorField(isRequired: boolean, control: FormControl): void {
    if (isRequired) {
      control.setValidators([Validators.required, Validators.min(1), Validators.max(2147483647)])
    } else {
      control.removeValidators([Validators.required, Validators.min(1), Validators.max(2147483647)])
    }

    control.updateValueAndValidity()
  }
}
